/**
 * Form Validation Utility
 * This module provides functions to validate form inputs based on specified criteria.
 */

// Define the CSS classes used for invalid states of form inputs.
const classIsInvalid = 'is-invalid';

/** * Initializes the validation state for specified input elements.
 * @param {Object} p_opt - Options for initialization.
 * @param {Array} p_opt.targetIds - Array of input element IDs to validate.
 * @param {HTMLElement} p_opt.scopeEle - The scope element within which to search for input elements.
 * */
const validateInit = (p_opt = {}) => {
    if (Object.keys(p_opt).length === 0) {return;}
    if (!p_opt.targetIds || !Array.isArray(p_opt.targetIds) || p_opt.targetIds.length === 0) {
        // console.error('Invalid parameters provided for initialization.');
        return; // Early exit if parameters are invalid
    }
    if (!p_opt.scopeEle || !p_opt.scopeEle.querySelector) {
        // console.error('scopeEle is not a valid DOM element or does not support querySelector.');
        return; // Early exit if scopeEle is invalid
    }
    // Remove the invalid class from all target input elements
    p_opt.targetIds.forEach((eleId) => {
        const targetInput = p_opt.scopeEle.querySelector('#' + eleId);
        if (targetInput) {
            targetInput.classList.remove(classIsInvalid);
        }
    });
}

/** * Validates form inputs based on specified criteria.
 * @param {Object} p_opt - Options for validation.
 * @param {Array} p_opt.targetIds - Array of input element IDs to validate.
 * @param {HTMLElement} p_opt.scopeEle - The scope element within which to search for input elements.
 * @param {Object} p_opt.params - An object containing the values of the input elements
 * @param {string} [p_id=null] - Optional specific input ID to validate. If provided, only this input will be validated.
 * @return {boolean} - Returns true if all inputs are valid, false otherwise.
 * */
const validateForm = (p_opt, p_id = null) => {
    let focusEle = null;
    let rtnValid = true;
    for (let i = 0; i < p_opt.targetIds.length; i++) {
        let isValid = true;

        if (p_id !== null && p_opt.targetIds[i] !== p_id) {
            continue; // Skip validation for other inputs if a specific ID is provided
        }
        // Get the input element by ID
        const eleId = p_opt.targetIds[i];
        if (!eleId || !p_opt.scopeEle) {
            console.error('Invalid parameters provided for validation.');
            return false; // Early exit if parameters are invalid
        }
        if (!p_opt.scopeEle.querySelector) {
            console.error('scopeEle does not support querySelector.');
            return false; // Early exit if scopeEle is invalid
        }
        // Validate the input element
        if (typeof eleId !== 'string' || eleId.trim() === '') {
            console.error(`Invalid element ID: '${eleId}'. It must be a non-empty string.`);
            continue; // Skip if the element ID is invalid
        }
        if (!p_opt.scopeEle.querySelector('#' + eleId)) {
            console.error(`Element with ID '${eleId}' not found in the provided scope.`);
            continue; // Skip if the element is not found
        }

        // Get the target input element
        const targetInput = p_opt.scopeEle.querySelector('#' + eleId);
        targetInput.classList.remove(classIsInvalid);

        // Validate the input value
        if (!targetInput) {
            console.error(`Target input with ID '${eleId}' not found.`);
            continue; // Skip if the target input is not found
        }
        
        // 1. Check if the input is required
        if (targetInput.getAttribute('required') !== null) {
            // If the input is required, check if it is empty
            if (targetInput.getAttribute('type') === 'checkbox') {
                // For checkbox, check if it is checked
                if (!targetInput.checked) {
                    isValid = false;
                }
            } else if (targetInput.getAttribute('type') === 'radio') {
                // For radio buttons, check if at least one is checked
                const radioGroup = p_opt.scopeEle.querySelectorAll('input[name="' + eleId + '"]');
                if (!Array.from(radioGroup).some(radio => radio.checked)) {
                    isValid = false;
                }
            } else {
                // For other input types, check if the value is empty
                if (targetInput.value === null || targetInput.value === undefined || targetInput.value.trim() === '') {
                    isValid = false;
                }
            }

            if (!isValid) {
                targetInput.nextElementSibling.innerHTML = 'This field is required.';
            }
        }
        // 2. Check if the input value matches the expected pattern
        if (isValid) {
            const emailPattern = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/;
            const telPattern = /^[\d()\s-]{0,20}$/;

            if (eleId.toLowerCase().indexOf('email') > -1 && !emailPattern.test(p_opt.params[eleId])) {
                isValid = false;
                targetInput.nextElementSibling.innerHTML = 'Please enter a valid email address.';

            } else if (eleId.toLowerCase().indexOf('telno') > -1 && !telPattern.test(p_opt.params[eleId])) {
                isValid = false;
                targetInput.nextElementSibling.innerHTML = 'Please enter a valid telephone number (10 or 11 digits).';
            }
        }

        // If the input is invalid, add the invalid class
        if (!isValid) {
            targetInput.classList.add(classIsInvalid);
            rtnValid = false; // Set the return value to false if any input is invalid

            if (focusEle === null) {
                focusEle = targetInput;
            }
        }
    }

    if (focusEle !== null) {
        // If any input is invalid, focus on the first invalid input
        focusEle.scrollIntoView({ behavior: 'smooth', block: 'center' });
        focusEle.focus();
    }

    return rtnValid;
};

export { validateInit };
export { validateForm };